import type { ComponentType } from 'react';
import {
  Database,
  UploadCloud,
  Wand2,
  Filter,
  BarChart3,
  GitBranch,
  Layers,
  Zap,
  Server,
} from 'lucide-react';
import { PipelineNodeType } from '../types/pipeline';

interface PaletteItem {
  type: PipelineNodeType;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  className: string;
  category?: 'basic' | 'executive';
}

const paletteItems: PaletteItem[] = [
  // Basic Nodes
  {
    type: 'SOURCE',
    label: 'Source',
    description: 'Extract data from sources',
    icon: Database,
    className: 'border-blue-200 bg-blue-50 text-blue-700',
    category: 'basic',
  },
  {
    type: 'TRANSFORMATION',
    label: 'Transform',
    description: 'Clean and shape data',
    icon: Wand2,
    className: 'border-amber-200 bg-amber-50 text-amber-700',
    category: 'basic',
  },
  {
    type: 'FILTER',
    label: 'Filter',
    description: 'Filter data rows',
    icon: Filter,
    className: 'border-purple-200 bg-purple-50 text-purple-700',
    category: 'basic',
  },
  {
    type: 'AGGREGATION',
    label: 'Aggregate',
    description: 'Group and aggregate',
    icon: BarChart3,
    className: 'border-orange-200 bg-orange-50 text-orange-700',
    category: 'basic',
  },
  {
    type: 'JOIN',
    label: 'Join',
    description: 'Join data streams',
    icon: GitBranch,
    className: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    category: 'basic',
  },
  {
    type: 'UNION',
    label: 'Union',
    description: 'Stack/append datasets',
    icon: Layers,
    className: 'border-teal-200 bg-teal-50 text-teal-700',
    category: 'basic',
  },
  {
    type: 'DESTINATION',
    label: 'Destination',
    description: 'Load to data warehouse',
    icon: UploadCloud,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    category: 'basic',
  },
  // Executive Dashboard Nodes
  {
    type: 'MULTI_SPLIT',
    label: 'Tier Split',
    description: 'Route to Hot/Warm/Cold stores',
    icon: Zap,
    className: 'border-red-200 bg-red-50 text-red-700',
    category: 'executive',
  },
  {
    type: 'TIERED_DESTINATION',
    label: 'Tiered Store',
    description: 'Redis/PostgreSQL/ClickHouse',
    icon: Server,
    className: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    category: 'executive',
  },
];

export default function NodePalette() {
  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    type: PipelineNodeType,
    label: string
  ) => {
    event.dataTransfer.setData('application/reactflow', type);
    event.dataTransfer.setData('application/reactflow-label', label);
    event.dataTransfer.effectAllowed = 'move';
  };

  const basicNodes = paletteItems.filter(item => item.category === 'basic');
  const executiveNodes = paletteItems.filter(item => item.category === 'executive');

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Node Palette
        </h2>
        <p className="text-xs text-muted-foreground">Drag nodes onto the canvas</p>
      </div>

      {/* Basic Nodes */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-muted-foreground">Basic ETL Nodes</h3>
        {basicNodes.map((item) => (
          <div
            key={item.type}
            draggable
            onDragStart={(event) => handleDragStart(event, item.type, item.label)}
            className={`flex cursor-grab items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium shadow-sm active:cursor-grabbing ${item.className}`}
          >
            <item.icon className="h-4 w-4" />
            <div className="flex-1">
              <div>{item.label}</div>
              <div className="text-xs text-muted-foreground">{item.description}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Executive Dashboard Nodes */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <Zap className="h-3 w-3" />
          Executive Dashboard Nodes
        </h3>
        {executiveNodes.map((item) => (
          <div
            key={item.type}
            draggable
            onDragStart={(event) => handleDragStart(event, item.type, item.label)}
            className={`flex cursor-grab items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium shadow-sm active:cursor-grabbing ${item.className}`}
          >
            <item.icon className="h-4 w-4" />
            <div className="flex-1">
              <div>{item.label}</div>
              <div className="text-xs text-muted-foreground">{item.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
